import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, DollarSign, Receipt, Send, Wallet, ShieldCheck, AlertTriangle, Save, Lock, Landmark } from "lucide-react";
import api, { API, timeAgo } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const Stat = ({ icon: Icon, label, value, sub }) => (
  <div className="bg-zinc-950 border border-white/10 p-5">
    <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-[0.15em]">
      <Icon className="h-4 w-4 text-[#FFD60A]" /> {label}
    </div>
    <p className="font-display text-2xl text-white mt-2">{value}</p>
    {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [summary, setSummary] = useState(null);
  const [txns, setTxns] = useState([]);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user && user.role === "admin") {
      api.get("/transactions/summary").then((r) => setSummary(r.data)).catch(() => {});
      api.get("/transactions").then((r) => setTxns(r.data)).catch(() => {});
      api.get("/settings").then((r) => setSettings({
        payout_email: r.data.payout_email || "",
        payout_method: r.data.payout_method || "paypal",
        tax_state: r.data.tax_state || "",
        tax_rate_pct: r.data.tax_rate ? (r.data.tax_rate * 100).toString() : "",
        fee: r.data.fee != null ? r.data.fee.toString() : "1.00",
        bank_last4: r.data.bank_last4 || "",
        card_last4: r.data.card_last4 || "",
        routing_number: r.data.routing_number || "",
        bank_account: "",
        card_number: "",
      })).catch(() => {});
    }
  }, [user]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {
        payout_email: settings.payout_email || undefined,
        payout_method: settings.payout_method,
        tax_state: settings.tax_state,
        tax_rate: settings.tax_rate_pct === "" ? 0 : parseFloat(settings.tax_rate_pct) / 100,
        fee: settings.fee === "" ? 1.0 : parseFloat(settings.fee),
        routing_number: settings.routing_number || undefined,
        bank_account: settings.bank_account || undefined,
        card_number: settings.card_number || undefined,
      };
      const { data } = await api.put("/settings", payload);
      toast.success("Settings saved");
      setSettings((s) => ({ ...s, bank_last4: data.bank_last4 || s.bank_last4, card_last4: data.card_last4 || s.card_last4, bank_account: "", card_number: "" }));
      api.get("/transactions/summary").then((r) => setSummary(r.data)).catch(() => {});
    } catch (e) {
      toast.error("Could not save settings");
    } finally { setSavingSettings(false); }
  };

  const downloadStatement = () => {
    const token = localStorage.getItem("nx_token");
    window.open(`${API}/transactions/statement.csv?token=${encodeURIComponent(token)}`, "_blank");
  };

  if (!summary) return <div className="h-[calc(100vh-6.5rem)] flex items-center justify-center text-zinc-500">Loading…</div>;

  const money = (n) => `$${(n || 0).toFixed(2)}`;

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 pb-24">
      <h1 className="font-display text-3xl sm:text-4xl text-white">Notification Center</h1>
      <p className="text-zinc-400 mt-2">Your share revenue, tax records, and payouts.</p>

      {/* PayPal status banner */}
      <div className={`mt-6 p-4 border flex items-center gap-3 ${summary.paypal_configured ? "bg-green-500/10 border-green-500/30 text-green-300" : "bg-[#FFD60A]/10 border-[#FFD60A]/30 text-[#FFD60A]"}`} data-testid="paypal-status">
        {summary.paypal_configured ? <ShieldCheck className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
        <div className="text-sm">
          {summary.paypal_configured
            ? <>PayPal connected — share fees are charged live and paid to <b>{summary.payout_email}</b>.</>
            : <>PayPal not connected yet. Share fees are <b>recorded</b> but not charged. Add your PayPal keys to go live. Payouts will go to <b>{summary.payout_email}</b>.</>}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Stat icon={Send} label="Total Shares" value={summary.count} sub={`${summary.paid_count} paid`} />
        <Stat icon={DollarSign} label="Revenue" value={money(summary.revenue)} sub="Share fees" />
        <Stat icon={Receipt} label="Tax Collected" value={money(summary.tax_collected)} sub={summary.tax_state ? `${summary.tax_state} @ ${(summary.tax_rate * 100).toFixed(3)}%` : "No state set"} />
        <Stat icon={Wallet} label="Gross Total" value={money(summary.gross_total)} sub="Fees + tax" />
      </div>

      {/* Payout & Tax settings */}
      {settings && (
        <div className="mt-10 bg-zinc-950 border border-white/10 p-6" data-testid="payout-settings">
          <h2 className="font-display text-xl text-white flex items-center gap-2"><Wallet className="h-5 w-5 text-[#FFD60A]" /> Payout &amp; Tax Settings</h2>
          <p className="text-zinc-500 text-sm mt-1">Revenue is paid to your PayPal. Tax is recorded for your records.</p>

          <div className="grid sm:grid-cols-2 gap-4 mt-5">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-zinc-400">PayPal email (payout)</label>
              <input data-testid="set-payout-email" value={settings.payout_email} onChange={(e) => setSettings({ ...settings, payout_email: e.target.value })} className="w-full mt-1.5 bg-zinc-900 border border-white/10 text-white px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFD60A]" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-zinc-400">Payout method</label>
              <select data-testid="set-payout-method" value={settings.payout_method} onChange={(e) => setSettings({ ...settings, payout_method: e.target.value })} className="w-full mt-1.5 bg-zinc-900 border border-white/10 text-white px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFD60A]">
                <option value="paypal">PayPal balance</option>
                <option value="bank">PayPal → Bank direct deposit</option>
                <option value="debit">PayPal → Debit card</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-zinc-400">Tax state</label>
              <input data-testid="set-tax-state" value={settings.tax_state} onChange={(e) => setSettings({ ...settings, tax_state: e.target.value })} placeholder="e.g. New York" className="w-full mt-1.5 bg-zinc-900 border border-white/10 text-white px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFD60A]" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-zinc-400">Tax rate (%)</label>
              <input data-testid="set-tax-rate" type="number" step="0.001" value={settings.tax_rate_pct} onChange={(e) => setSettings({ ...settings, tax_rate_pct: e.target.value })} placeholder="e.g. 8.875" className="w-full mt-1.5 bg-zinc-900 border border-white/10 text-white px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFD60A]" />
            </div>
          </div>

          {/* Bank / debit (optional) */}
          <div className="mt-5 border-t border-white/10 pt-4">
            <p className="text-sm text-zinc-300 flex items-center gap-2"><Landmark className="h-4 w-4 text-[#FFD60A]" /> Bank / Debit for direct deposit <span className="text-zinc-500">(optional)</span></p>
            <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1"><Lock className="h-3 w-3" /> For your security we store only the <b className="text-zinc-300">last 4 digits</b>. Full transfers are handled inside PayPal.</p>
            <div className="grid sm:grid-cols-3 gap-4 mt-3">
              <div>
                <label className="text-xs text-zinc-400">Routing number</label>
                <input data-testid="set-routing" value={settings.routing_number} onChange={(e) => setSettings({ ...settings, routing_number: e.target.value })} className="w-full mt-1.5 bg-zinc-900 border border-white/10 text-white px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFD60A]" />
              </div>
              <div>
                <label className="text-xs text-zinc-400">Account number {settings.bank_last4 && <span className="text-zinc-500">(••{settings.bank_last4})</span>}</label>
                <input data-testid="set-account" value={settings.bank_account} onChange={(e) => setSettings({ ...settings, bank_account: e.target.value })} placeholder="enter to update" className="w-full mt-1.5 bg-zinc-900 border border-white/10 text-white px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFD60A]" />
              </div>
              <div>
                <label className="text-xs text-zinc-400">Debit card {settings.card_last4 && <span className="text-zinc-500">(••{settings.card_last4})</span>}</label>
                <input data-testid="set-card" value={settings.card_number} onChange={(e) => setSettings({ ...settings, card_number: e.target.value })} placeholder="enter to update" className="w-full mt-1.5 bg-zinc-900 border border-white/10 text-white px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFD60A]" />
              </div>
            </div>
          </div>

          <button onClick={saveSettings} disabled={savingSettings} data-testid="save-settings" className="mt-5 flex items-center gap-2 bg-[#FFD60A] text-black px-5 py-2.5 font-semibold hover:bg-[#FFD60A]/90 transition-colors disabled:opacity-60">
            <Save className="h-4 w-4" /> {savingSettings ? "Saving…" : "Save settings"}
          </button>
        </div>
      )}

      <div className="flex items-center justify-between mt-10 mb-4">
        <h2 className="font-display text-xl text-white">Transactions</h2>
        <button onClick={downloadStatement} data-testid="download-statement" className="flex items-center gap-2 bg-[#FFD60A] text-black px-4 py-2 text-sm font-semibold hover:bg-[#FFD60A]/90 transition-colors">
          <Download className="h-4 w-4" /> Tax statement (CSV)
        </button>
      </div>

      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-950 text-zinc-400 text-xs uppercase tracking-wide">
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Video</th>
              <th className="text-left px-4 py-3">Artist</th>
              <th className="text-right px-4 py-3">Fee</th>
              <th className="text-right px-4 py-3">Tax</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-left px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {txns.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">No share transactions yet.</td></tr>
            ) : txns.map((t) => (
              <tr key={t.id} className="border-t border-white/5 text-zinc-200" data-testid={`txn-${t.id}`}>
                <td className="px-4 py-3 text-zinc-400">{timeAgo(t.created_at)}</td>
                <td className="px-4 py-3">{t.video_title}</td>
                <td className="px-4 py-3 text-zinc-400">{t.artist_name}</td>
                <td className="px-4 py-3 text-right">{money(t.fee)}</td>
                <td className="px-4 py-3 text-right">{money(t.tax)}</td>
                <td className="px-4 py-3 text-right font-semibold">{money(t.total)}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${t.status === "paid" ? "bg-green-500/20 text-green-300" : t.status === "free_owner" ? "bg-blue-500/20 text-blue-300" : "bg-[#FFD60A]/20 text-[#FFD60A]"}`}>
                    {t.status === "free_owner" ? "free (owner)" : t.status === "pending_payment" ? "pending" : t.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Dashboard;
